// src/ui/CallRatingCard.jsx — "How was the call?" in the browser.
//
// The twin of allo-app/src/components/CallRatingCard.tsx: same three steps, same
// server, same account-level cooldown. That last part is what makes moving a
// call between devices behave — the browser and the phone ask ONE history, so a
// call taken on the laptop does not earn a second prompt on the phone.
//
// 🟥 IT RENDERS NOTHING UNTIL THE SERVER SAYS YES. Not a spinner, not an empty
//    shell — a card that appears and then vanishes is worse than one that never
//    appeared.
//
// 🟢 AND IT IS EASY TO IGNORE. Backdrop, Escape, the ✕, or simply navigating
//    away all dismiss it, and all of them are reported — because a prompt
//    someone walked away from is a prompt they did not want. Two in a row and
//    the server stops asking for a month.

import React, { useEffect, useRef, useState } from 'react';
import { X } from './icons';
import {
  shouldAskRating, rateCall, sendRatingReason, sendCallLogs, dismissRating,
} from '../services/callRating';

const CHOICES = [
  { id: 'bad', emoji: '🙁', label: 'Bad' },
  { id: 'okay', emoji: '😐', label: 'Okay' },
  { id: 'good', emoji: '🙂', label: 'Good' },
];

export default function CallRatingCard({ call, onClose }) {
  const [ask, setAsk] = useState(null);
  const [step, setStep] = useState('rate');
  const [rating, setRating] = useState(null);
  const [reason, setReason] = useState(null);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  /* 🟥 A LATCH, NOT A FLAG READ AT UNMOUNT. Without it the cleanup below fires
     after a completed flow and tells the server they ignored a card they had
     just filled in — pushing co-operative people toward the backoff. */
  const answered = useRef(false);
  /* 🟥 AND `shown` — THE GUARD THAT WAS MISSING, AND THE REASON THIS FEATURE WENT
     DEAD ON A REAL ACCOUNT. This component MOUNTS after every ended call and then
     asks the server whether it may exist. When the answer is no it calls onClose()
     and unmounts — and the unmount cleanup below reported that as a DISMISSAL, for
     a card nobody ever saw. Every "no" recorded a "no thanks", which is
     self-reinforcing: two of them and the account is in `dismissed_repeatedly` for
     thirty days, which produces more refusals, which record more dismissals.

     Set in exactly one place — the moment the server grants the ask — so a
     dismissal can only ever be reported for a card that was on screen. */
  const shown = useRef(false);

  useEffect(() => {
    let live = true;
    shouldAskRating(call)
      .then((r) => {
        if (!live) return;
        if (r?.ask) {
          shown.current = true;   // 🟥 the ONLY place this becomes true
          setAsk({ signals: r.signals || [], reasons: r.reasons || [] });
        } else onClose();
      })
      .catch(() => { if (live) onClose(); });
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* 🟥 UNMOUNT IS THE COMMONEST DISMISSAL. Closing the tab, clicking into a
     chat, reloading — none of those touch a button, and all of them mean no. */
  useEffect(() => () => {
    if (shown.current && !answered.current) dismissRating().catch(() => {});
  }, []);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') bail(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const bail = () => {
    if (shown.current && !answered.current) dismissRating().catch(() => {});
    onClose();
  };

  const choose = async (r) => {
    if (busy) return;
    setBusy(true); setRating(r); answered.current = true;
    try {
      const out = await rateCall({ ...call, rating: r });
      if (out?.askWhy) setStep('why');
      else { setStep('thanks'); setTimeout(onClose, 1200); }
    } catch { onClose(); } finally { setBusy(false); }
  };

  const submitWhy = async (skip) => {
    if (busy) return;
    setBusy(true);
    try {
      if (!skip && (reason || text.trim())) {
        await sendRatingReason({ callId: call.callId, reason, text: text.trim() });
      }
    } catch { /* the reason is optional; never trap them here */ }
    setStep('logs'); setBusy(false);
  };

  const logs = async (yes) => {
    if (busy) return;
    if (!yes) { setStep('thanks'); setTimeout(onClose, 900); return; }
    setBusy(true);
    try { await sendCallLogs(call); } catch { /* best effort */ }
    setStep('thanks'); setTimeout(onClose, 1100); setBusy(false);
  };

  if (!ask) return null;

  return (
    <div className="crc-back" onMouseDown={bail} role="presentation">
      <div className="crc" onMouseDown={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        <button className="crc-x icon-btn" onClick={bail} aria-label="Close"><X /></button>

        {step === 'rate' && (
          <>
            <h3>How was the call?</h3>
            <p className="crc-sub">We only ask now and then. It helps us find the calls that go wrong.</p>
            <div className="crc-choices">
              {CHOICES.map((c) => (
                <button
                  key={c.id}
                  className={`crc-choice ${rating === c.id ? 'on' : ''}`}
                  disabled={busy}
                  onClick={() => choose(c.id)}
                >
                  <span className="crc-emoji" aria-hidden>{c.emoji}</span>
                  <span>{c.label}</span>
                </button>
              ))}
            </div>
          </>
        )}

        {step === 'why' && (
          <>
            <h3>Is it okay to ask why?</h3>
            <p className="crc-sub">Pick the closest one, or write it in your own words. Both are optional.</p>
            <div className="crc-reasons">
              {(ask.reasons || []).map((r) => (
                <button
                  key={r.id}
                  className={`crc-reason ${reason === r.id ? 'on' : ''}`}
                  onClick={() => setReason(reason === r.id ? null : r.id)}
                >
                  {r.label}
                </button>
              ))}
            </div>
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              rows={2}
              placeholder="Anything else? (optional)"
              maxLength={1000}
            />
            <div className="crc-actions">
              <button className="crc-btn ghost" onClick={() => submitWhy(true)}>Skip</button>
              <button className="crc-btn" disabled={busy} onClick={() => submitWhy(false)}>
                {busy ? 'Sending…' : 'Continue'}
              </button>
            </div>
          </>
        )}

        {step === 'logs' && (
          <>
            <h3>May we look at what happened?</h3>
            {/* 🟢 NAME WHAT WE NOTICED. "We saw the picture froze — may we see the
                details?" is a different question from "may we have your
                diagnostics?", and it is the one people can actually answer. */}
            {ask.signals?.length > 0 && (
              <p className="crc-sub">We noticed: {ask.signals.join(', ')}.</p>
            )}
            <p className="crc-sub">
              We can send a technical report about this call — connection speed, packet loss,
              and how your video quality changed.{' '}
              {/* 🟥 SAY WHAT IT IS NOT. People hear "logs" and think we mean their
                  conversation. */}
              <b>It contains no messages, no audio and no video — nothing that was said.</b>
            </p>
            <div className="crc-actions">
              <button className="crc-btn ghost" onClick={() => logs(false)}>No thanks</button>
              <button className="crc-btn" disabled={busy} onClick={() => logs(true)}>
                {busy ? 'Sending…' : 'Send report'}
              </button>
            </div>
          </>
        )}

        {step === 'thanks' && (
          <div className="crc-thanks">
            <h3>Thank you</h3>
            <p className="crc-sub">This goes straight to the team that works on calls.</p>
          </div>
        )}
      </div>
    </div>
  );
}
